/* NIVIÈREE · botão VER MAIS na página de coleção · 26-10-2026
 *
 * Substitui a rolagem infinita. A coleção mostra a primeira leva de produtos e, embaixo,
 * um botão "VER MAIS" com a contagem "Vendo 1-24 de 154 produtos" (como a Pallacio).
 * Ao clicar, os produtos da página seguinte são buscados e entram no fim da lista.
 * A paginação do tema continua no código (sem JavaScript e para o Google), mas fica
 * escondida; o endereço da próxima página é lido do link "próxima" dela e o total de
 * produtos vem do atributo data-total-items da própria paginação.
 *
 * - Filtros e ordenação redesenham a seção; o botão recomeça sozinho na lista nova.
 * - Ao abrir um produto e voltar, as páginas que já tinham carregado são buscadas de
 *   novo e a tela volta para o mesmo ponto.
 * - Se a busca falhar, o botão vira "TENTAR DE NOVO".
 * - Os cards novos são preparados pelos outros scripts da grade (cores, visualização,
 *   favoritos), que já observam a lista.
 */
(() => {
  if (window.__nvrInfiniteScroll) return;
  window.__nvrInfiniteScroll = true;

  const SECTION = '.shopify-section--main-collection';
  const RESTORE_KEY = 'nvr-infinite-restore';
  const LABEL_MORE = 'VER MAIS';
  const LABEL_RETRY = 'TENTAR DE NOVO';

  let watched = null;
  let loading = false;

  const getSection = () => document.querySelector(SECTION);
  const getList = () => getSection()?.querySelector('.product-list') || null;
  const sectionId = () => (getSection()?.id || '').replace('shopify-section-', '');

  const currentPageOf = (root) => {
    const text = root.querySelector('.collection__pagination .pagination__current')?.textContent || '';
    return parseInt(text, 10) || 1;
  };

  const nextUrlOf = (root) => root.querySelector('.collection__pagination a[rel="next"]')?.getAttribute('href') || '';

  const totalOf = (root) => parseInt(root.querySelector('.collection__pagination')?.dataset.totalItems || '', 10) || 0;

  // Produtos de verdade na lista (os blocos de promoção não contam).
  const countCards = (list) =>
    Array.from(list.children).filter((item) => !item.classList.contains('product-list__promo')).length;

  // Cria o botão logo depois da paginação (só existe com 2+ páginas).
  const getSentinel = () => {
    const section = getSection();
    if (!section) return null;

    const existing = section.querySelector('[data-nvr-infinite]');
    if (existing) return existing;

    const pagination = section.querySelector('.collection__pagination');
    if (!pagination || !section.querySelector('.product-list')) return null;

    const sentinel = document.createElement('div');
    sentinel.className = 'nvr-infinite';
    sentinel.setAttribute('data-nvr-infinite', '');
    sentinel.dataset.nextUrl = nextUrlOf(section);
    sentinel.dataset.page = String(currentPageOf(section));
    sentinel.dataset.total = String(totalOf(section));
    sentinel.innerHTML =
      '<button type="button" class="nvr-infinite__more" data-nvr-infinite-more>' +
      '<span class="nvr-infinite__label" data-nvr-infinite-label>' + LABEL_MORE + '</span>' +
      '<span class="nvr-infinite__loader" aria-hidden="true"><i></i><i></i><i></i></span>' +
      '</button>' +
      '<p class="nvr-infinite__count" data-nvr-infinite-count></p>' +
      '<span class="sr-only" role="status" data-nvr-infinite-status></span>';
    pagination.after(sentinel);
    return sentinel;
  };

  const setStatus = (sentinel, text) => {
    const status = sentinel.querySelector('[data-nvr-infinite-status]');
    if (status) status.textContent = text;
  };

  const setLabel = (sentinel, text) => {
    const label = sentinel.querySelector('[data-nvr-infinite-label]');
    if (label) label.textContent = text;
  };

  // "Vendo 1-24 de 154 produtos"
  const updateCount = (sentinel) => {
    const list = getList();
    const target = sentinel.querySelector('[data-nvr-infinite-count]');
    if (!list || !target) return;
    const shown = countCards(list);
    const total = Math.max(Number(sentinel.dataset.total) || 0, shown);
    target.textContent = 'Vendo 1-' + shown + ' de ' + total + ' produtos';
  };

  const fetchPage = async (url) => {
    const target = new URL(url, window.location.origin);
    const id = sectionId();
    if (id) target.searchParams.set('section_id', id);
    target.searchParams.set('_nvr_page', String(Date.now()));
    const response = await fetch(target.toString(), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  // Mesma entrada dos primeiros cards (o tema só anima a primeira leva).
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fadeIn = (cards) => {
    if (reduceMotion.matches || !Element.prototype.animate) return;
    cards.forEach((card, index) => {
      card.animate(
        [{ opacity: 0, transform: 'translateY(15px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 350, delay: Math.min(index, 8) * 50, easing: 'ease', fill: 'backwards' }
      );
    });
  };

  const loadNext = async () => {
    const sentinel = getSentinel();
    const list = getList();
    if (loading || !sentinel || !list || !sentinel.dataset.nextUrl) return false;

    loading = true;
    sentinel.classList.add('is-loading');
    sentinel.classList.remove('has-error');
    setLabel(sentinel, LABEL_MORE);
    setStatus(sentinel, 'Carregando mais produtos');

    try {
      const doc = await fetchPage(sentinel.dataset.nextUrl);

      // Filtro ou ordenação trocaram a seção enquanto a página carregava: descarta.
      if (sentinel !== getSentinel()) return false;

      const nextList = doc.querySelector('.product-list');
      const items = nextList
        ? Array.from(nextList.children).filter((item) => !item.classList.contains('product-list__promo'))
        : [];

      const cardKey = (item) => {
        const card = item.matches('product-card') ? item : item.querySelector('product-card');
        return card?.getAttribute('handle') || '';
      };
      const existing = new Set(Array.from(list.children, cardKey).filter(Boolean));
      const fragment = document.createDocumentFragment();
      const added = [];
      for (const item of items) {
        const key = cardKey(item);
        if (key && existing.has(key)) continue;
        if (key) existing.add(key);
        added.push(fragment.appendChild(document.importNode(item, true)));
      }
      list.appendChild(fragment);
      fadeIn(added);

      sentinel.dataset.nextUrl = nextUrlOf(doc);
      sentinel.dataset.page = String(currentPageOf(doc));
      sentinel.dataset.total = String(totalOf(doc) || Number(sentinel.dataset.total) || 0);
      sentinel.classList.toggle('is-done', !sentinel.dataset.nextUrl);
      updateCount(sentinel);
      setStatus(sentinel, added.length + ' produtos carregados');
      return true;
    } catch (_error) {
      if (sentinel === getSentinel()) {
        sentinel.classList.add('has-error');
        setLabel(sentinel, LABEL_RETRY);
        setStatus(sentinel, 'Não foi possível carregar mais produtos');
      }
      return false;
    } finally {
      loading = false;
      sentinel.classList.remove('is-loading');
    }
  };

  /* ---------- Voltar do produto para o mesmo ponto ---------- */

  const pageKey = () => window.location.pathname + window.location.search;

  const rememberPosition = (event) => {
    const link = event.target.closest?.('a[href]');
    const sentinel = getSentinel();
    if (!link || !sentinel || !link.closest(SECTION + ' .product-list')) return;
    try {
      sessionStorage.setItem(RESTORE_KEY, JSON.stringify({
        key: pageKey(),
        page: Number(sentinel.dataset.page) || 1,
        y: window.scrollY,
      }));
    } catch (_e) { /* sem armazenamento: volta para o topo da lista */ }
  };

  const readRestore = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(RESTORE_KEY) || 'null');
      sessionStorage.removeItem(RESTORE_KEY);
      return saved;
    } catch (_e) {
      return null;
    }
  };

  const restorePosition = async () => {
    const navigation = performance.getEntriesByType?.('navigation')[0];
    const saved = readRestore();
    if (!navigation || navigation.type !== 'back_forward' || !saved || saved.key !== pageKey()) return;

    const sentinel = getSentinel();
    if (!sentinel || (Number(sentinel.dataset.page) || 1) >= saved.page) return;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    let guard = 0;
    while ((Number(sentinel.dataset.page) || 1) < saved.page && sentinel.dataset.nextUrl && guard < 20) {
      guard += 1;
      if (!(await loadNext())) break;
    }
    // "instant": o tema usa rolagem suave, que aqui faria a tela deslizar até o ponto.
    const jump = () => window.scrollTo({ top: saved.y, left: 0, behavior: 'instant' });
    jump();
    window.setTimeout(jump, 250);
  };

  /* ---------- Início ---------- */

  const init = () => {
    const sentinel = getSentinel();
    if (sentinel === watched) return;

    watched = sentinel;
    if (!sentinel) return;

    getSection().classList.add('nvr-infinite-on');
    sentinel.classList.toggle('is-done', !sentinel.dataset.nextUrl);
    updateCount(sentinel);
  };

  const start = () => {
    init();
    restorePosition();

    document.addEventListener('click', (event) => {
      const more = event.target.closest?.('[data-nvr-infinite-more]');
      if (more) {
        event.preventDefault();
        loadNext();
        return;
      }
      rememberPosition(event);
    }, true);

    // Filtros e ordenação redesenham a seção: liga o botão na lista nova.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.setTimeout(() => {
        queued = false;
        if (getSentinel() !== watched) init();
      }, 30);
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
